import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { googleAuthApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * AuthCallback - Handles Google OAuth callback
 * 
 * This component processes the session_id from the URL fragment after Google OAuth redirect.
 * It exchanges the session_id for a session token and redirects to the dashboard.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setGoogleUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/', { replace: true });
          return;
        }

        // Exchange session_id for user data and session cookie
        const userData = await googleAuthApi.exchangeSession(sessionId);

        console.log('Google OAuth successful:', userData.email);

        // CRITICAL: push the freshly authenticated user into AuthContext so
        // the Navbar (and other consumers) re-render immediately showing the
        // authenticated UI (Profile, Logout, etc). Without this, context stays
        // null until a full page reload, causing the "logout button missing"
        // inconsistency after Google sign-in.
        setGoogleUser(userData);

        // Clear the URL fragment
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setGoogleUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#00FF00] mx-auto mb-4" />
        <p className="text-white text-lg">Authenticating...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
